import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export interface VillaAvailabilityResult {
  available: Set<string>;
  checkIn: string;
  checkOut: string;
}

interface OurVillasSectionProps {
  availability?: VillaAvailabilityResult | null;
}

const villas = [
  {
    name: 'Villa Buenlugar',
    villaSlug: 'villa-buenlugar',
    slug: '/villa-buenlugar',
    taglineKey: 'villa_buenlugar_tagline',
    guests: 4,
    bedrooms: 2,
    bathrooms: 2,
    price: '€160',
    image: 'https://storage.helloreaddy.io/project_files/f11cd3cf-6b55-4cb4-bc65-73427384328d/c0f865ac-009b-4974-b6cc-bf4b87855c06_compressed_Captura-de-pantalla-2026-07-16-a-las-14.20.25.webp',
  },
  {
    name: 'Casa De la Villaflor',
    villaSlug: 'casa-de-la-villaflor',
    slug: '/casa-de-la-villaflor',
    taglineKey: 'villa_villaflor_tagline',
    guests: 4,
    bedrooms: 2,
    bathrooms: 2,
    price: '€160',
    image: 'https://storage.helloreaddy.io/project_files/f11cd3cf-6b55-4cb4-bc65-73427384328d/744e5944-e504-4454-9f4f-5325c6999fa8_compressed_F5E3E739-2C7B-401F-AAC7-7110DF8776C9.webp',
  },
  {
    name: 'Casa de El Golfo',
    villaSlug: 'casa-de-el-golfo',
    slug: '/casa-de-el-golfo',
    taglineKey: 'villa_golfo_tagline',
    guests: 4,
    bedrooms: 2,
    bathrooms: 1,
    price: '€80',
    image: 'https://storage.helloreaddy.io/project_files/f11cd3cf-6b55-4cb4-bc65-73427384328d/413c3362-6caa-463f-abce-86e84d27420f_compressed_Captura-de-pantalla-2026-07-16-a-las-14.12.28.webp',
  },
];

export default function OurVillasSection({ availability }: OurVillasSectionProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const searched = availability != null;
  const availableCount = searched
    ? villas.filter((v) => availability!.available.has(v.villaSlug)).length
    : 0;

  return (
    <section id="villas" className="py-16 md:py-24 px-4 md:px-10 bg-background-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] text-foreground-500 font-medium mb-3">
            {t('villas_subtitle')}
          </p>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground-950">
            {t('villas_title')}
          </h2>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
            {t('villas_description')}
          </p>
        </div>

        {searched && (
          <div
            className={`mb-8 flex flex-col sm:flex-row items-center justify-center gap-2 px-4 py-3 rounded-md border ${
              availableCount > 0
                ? 'bg-accent-50 border-accent-200 text-accent-900'
                : 'bg-accent-50 border-accent-200 text-accent-800'
            }`}
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i
                className={`text-base ${
                  availableCount > 0 ? 'ri-checkbox-circle-fill' : 'ri-close-circle-line'
                }`}
              ></i>
            </span>
            {availableCount > 0 ? (
              <span className="text-sm font-medium whitespace-nowrap">
                {availableCount} {t('villas_available_for_dates')}
              </span>
            ) : (
              <span className="text-sm font-medium">{t('villas_no_results')}</span>
            )}
            <span className="text-xs text-foreground-500 whitespace-nowrap">
              {availability!.checkIn} → {availability!.checkOut}
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {villas.map((villa) => {
            const isOccupied = searched && !availability!.available.has(villa.villaSlug);
            const isAvailable = searched && availability!.available.has(villa.villaSlug);

            return (
              <div
                key={villa.name}
                className={`bg-background-50 rounded-lg overflow-hidden group border transition-all duration-300 relative ${
                  isOccupied
                    ? 'border-background-200 opacity-50'
                    : 'border-background-200 hover:border-primary-300 cursor-pointer'
                }`}
                onClick={() => {
                  if (!isOccupied) navigate(villa.slug);
                }}
                role="button"
                tabIndex={isOccupied ? -1 : 0}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && !isOccupied) {
                    navigate(villa.slug);
                  }
                }}
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={villa.image}
                    alt={`${villa.name} - vacation home in Lanzarote with ${t(villa.taglineKey).toLowerCase()}`}
                    className={`w-full h-full object-cover object-center transition-transform duration-500 ${
                      isOccupied ? '' : 'group-hover:scale-105'
                    } ${isOccupied ? 'grayscale' : ''}`}
                    loading="lazy"
                  />
                  <div className="absolute top-3 left-3 bg-background-50/90 backdrop-blur-sm px-3 py-1.5 rounded-md">
                    <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">
                      {t('villas_from')} {villa.price}
                      {t('villas_night')}
                    </p>
                  </div>
                  {isAvailable && (
                    <div className="absolute top-3 right-3 bg-accent-500 text-white px-3 py-1.5 rounded-md">
                      <p className="text-xs font-semibold whitespace-nowrap">
                        {t('villas_available')}
                      </p>
                    </div>
                  )}
                  {isOccupied && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="bg-background-50/90 text-foreground-900 px-4 py-2 rounded-md text-sm font-semibold whitespace-nowrap">
                        {t('villas_not_available')}
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-5 md:p-6">
                  <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-1">
                    {villa.name}
                  </h3>
                  <p className="text-sm text-primary-600 font-medium mb-3">{t(villa.taglineKey)}</p>
                  <div className="flex items-center gap-4 text-xs text-foreground-500">
                    <div className="flex items-center gap-1">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-user-star-line text-sm"></i>
                      </span>
                      <span>
                        {villa.guests} {t('villas_guests')}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-hotel-bed-line text-sm"></i>
                      </span>
                      <span>
                        {villa.bedrooms} {t('villas_bedrooms')}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-drop-line text-sm"></i>
                      </span>
                      <span>
                        {villa.bathrooms} {t('villas_bathrooms')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}